import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Aurora text input. 40px on desktop, h-10 to match `Button` default.
 * Touch targets remain comfortable because labels above + 32px+ tap surface
 * inside the field still ratchet the actionable area past 44px.
 */
function Input({ className, type, ...props }: React.ComponentProps<'input'>) {
  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        'flex h-10 w-full min-w-0 rounded-md border border-input bg-card px-3.5 text-sm shadow-soft transition-[box-shadow,border-color] duration-150 ease-out',
        'placeholder:text-muted-foreground/80',
        'selection:bg-primary/15 selection:text-foreground',
        'file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
        'outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
        'dark:bg-card/60',
        'md:text-sm',
        className,
      )}
      {...props}
    />
  )
}

/**
 * Auto-growing textarea sibling of `Input`, with the same Aurora styling.
 * Auto-grow handled by the consumer (textarea has many use cases) — this
 * is just the visual chrome.
 */
function Textarea({ className, ...props }: React.ComponentProps<'textarea'>) {
  return (
    <textarea
      data-slot="textarea"
      className={cn(
        'flex min-h-20 w-full rounded-md border border-input bg-card px-3.5 py-2.5 text-sm shadow-soft transition-[box-shadow,border-color] duration-150 ease-out',
        'placeholder:text-muted-foreground/80',
        'outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
        'dark:bg-card/60',
        className,
      )}
      {...props}
    />
  )
}

export { Input, Textarea }
